import React, { useState, useEffect, useRef } from 'react';
import { Input } from './ui/input';
import { MapPin } from 'lucide-react';

// City autocomplete using GeoNames API (free)
// This component provides city suggestions with auto-fill for country/state

const GEONAMES_API = 'https://secure.geonames.org/searchJSON';
const GEONAMES_USERNAME = 'demo'; // Free demo account - limited requests

export const CityAutocomplete = ({ 
  value, 
  onChange, 
  onCitySelect,
  placeholder = "Search city...",
  className = ""
}) => {
  const [query, setQuery] = useState(value || '');
  const [suggestions, setSuggestions] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const wrapperRef = useRef(null);
  const debounceRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Update internal state when value prop changes
  useEffect(() => {
    setQuery(value || '');
  }, [value]);

  // Debounced search
  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    if (query.length < 2) {
      setSuggestions([]);
      return;
    }

    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const response = await fetch(
          `${GEONAMES_API}?q=${encodeURIComponent(query)}&maxRows=8&featureClass=P&username=${GEONAMES_USERNAME}`
        );
        const data = await response.json();
        
        if (data.geonames) {
          const cities = data.geonames.map(city => ({
            name: city.name,
            country: city.countryName,
            countryCode: city.countryCode,
            state: city.adminName1 || '',
            population: city.population,
            lat: city.lat,
            lng: city.lng
          }));
          setSuggestions(cities);
          setIsOpen(true);
        }
      } catch (error) {
        console.error('City search error:', error);
        // Fallback to static suggestions
        setSuggestions(getStaticSuggestions(query));
        setIsOpen(true);
      }
      setLoading(false);
    }, 300);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [query]);

  const handleSelect = (city) => {
    setQuery(city.name);
    onChange?.(city.name);
    onCitySelect?.({
      city: city.name,
      state: city.state,
      country: city.country,
      countryCode: city.countryCode
    });
    setIsOpen(false);
  };

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setQuery(newValue);
    onChange?.(newValue);
  };

  return (
    <div ref={wrapperRef} className="relative">
      <div className="relative">
        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          value={query}
          onChange={handleInputChange}
          onFocus={() => suggestions.length > 0 && setIsOpen(true)}
          placeholder={placeholder}
          className={`pl-10 ${className}`}
          autoComplete="off"
        />
        {loading && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2">
            <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        )}
      </div>
      
      {isOpen && suggestions.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-border rounded-md shadow-lg max-h-60 overflow-auto">
          {suggestions.map((city, index) => (
            <button
              key={`${city.name}-${city.countryCode}-${index}`}
              type="button"
              onClick={() => handleSelect(city)}
              className="w-full px-4 py-2 text-left hover:bg-stone-100 transition-colors flex items-center justify-between"
            >
              <div>
                <span className="font-medium">{city.name}</span>
                {city.state && <span className="text-muted-foreground">, {city.state}</span>}
              </div>
              <span className="text-sm text-muted-foreground">{city.country}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// Fallback static suggestions for common cities
const getStaticSuggestions = (query) => {
  const cities = [
    { name: 'Milano', country: 'Italy', countryCode: 'IT', state: 'Lombardia' },
    { name: 'Roma', country: 'Italy', countryCode: 'IT', state: 'Lazio' },
    { name: 'Torino', country: 'Italy', countryCode: 'IT', state: 'Piemonte' },
    { name: 'Firenze', country: 'Italy', countryCode: 'IT', state: 'Toscana' },
    { name: 'Napoli', country: 'Italy', countryCode: 'IT', state: 'Campania' },
    { name: 'Bologna', country: 'Italy', countryCode: 'IT', state: 'Emilia-Romagna' },
    { name: 'Venezia', country: 'Italy', countryCode: 'IT', state: 'Veneto' },
    { name: 'Verona', country: 'Italy', countryCode: 'IT', state: 'Veneto' },
    { name: 'Genova', country: 'Italy', countryCode: 'IT', state: 'Liguria' },
    { name: 'Palermo', country: 'Italy', countryCode: 'IT', state: 'Sicilia' },
    { name: 'New York', country: 'United States', countryCode: 'US', state: 'New York' },
    { name: 'Los Angeles', country: 'United States', countryCode: 'US', state: 'California' },
    { name: 'London', country: 'United Kingdom', countryCode: 'GB', state: 'England' },
    { name: 'Paris', country: 'France', countryCode: 'FR', state: 'Île-de-France' },
    { name: 'Berlin', country: 'Germany', countryCode: 'DE', state: 'Berlin' },
    { name: 'Madrid', country: 'Spain', countryCode: 'ES', state: 'Madrid' },
    { name: 'Barcelona', country: 'Spain', countryCode: 'ES', state: 'Cataluña' },
    { name: 'Dubai', country: 'United Arab Emirates', countryCode: 'AE', state: 'Dubai' },
    { name: 'Singapore', country: 'Singapore', countryCode: 'SG', state: '' },
    { name: 'Tokyo', country: 'Japan', countryCode: 'JP', state: 'Tokyo' },
  ];
  
  const q = query.toLowerCase();
  return cities.filter(c => 
    c.name.toLowerCase().includes(q) || 
    c.country.toLowerCase().includes(q)
  ).slice(0, 8);
};

export default CityAutocomplete;
